// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/caixa/hooks/useCaixa.ts
// ════════════════════════════════════════════════════════════════════════════

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { caixaService } from '../services/caixaService';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import type { CriarLancamentoCaixaRequest, ReforcoSangriaRequest, ConciliarLoteRequest } from '../types';

export const useCaixaSaldo = (caixaId: number) =>
  useQuery({
    queryKey: ['caixa-saldo', caixaId],
    queryFn: () => caixaService.getSaldo(caixaId),
    enabled: caixaId > 0,
  });

export const useCaixaPeriodo = (inicio: string, fim: string) =>
  useQuery({
    queryKey: ['caixa-periodo', inicio, fim],
    queryFn: () => caixaService.getPorPeriodo(inicio, fim),
    enabled: !!inicio && !!fim,
  });

export const useCaixaExtrato = (caixaId: number, inicio?: string, fim?: string) =>
  useQuery({
    queryKey: ['caixa-extrato', caixaId, inicio, fim],
    queryFn: () => caixaService.getExtrato(caixaId, inicio, fim),
    enabled: caixaId > 0,
  });

export const useCaixaRegistrar = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CriarLancamentoCaixaRequest) => caixaService.registrar(request),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['caixa-periodo'] });
      queryClient.invalidateQueries({ queryKey: ['caixa-extrato', vars.caixaId] });
      queryClient.invalidateQueries({ queryKey: ['caixa-saldo', vars.caixaId] });
      toast.success('Lançamento registrado com sucesso');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
};

export const useCaixaReforco = (caixaId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: ReforcoSangriaRequest) => caixaService.reforco(caixaId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['caixa-extrato', caixaId] });
      queryClient.invalidateQueries({ queryKey: ['caixa-saldo', caixaId] });
      toast.success('Reforço registrado com sucesso');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
};

export const useCaixaSangria = (caixaId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: ReforcoSangriaRequest) => caixaService.sangria(caixaId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['caixa-extrato', caixaId] });
      queryClient.invalidateQueries({ queryKey: ['caixa-saldo', caixaId] });
      toast.success('Sangria registrada com sucesso');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
};

export const useCaixaConciliar = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => caixaService.conciliar(id),
    onSuccess: (resultado) => {
      queryClient.invalidateQueries({ queryKey: ['caixa-extrato'] });
      toast.success(resultado.mensagem);
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
};

export const useCaixaConciliarLote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: ConciliarLoteRequest) => caixaService.conciliarLote(req),
    onSuccess: (resultados) => {
      queryClient.invalidateQueries({ queryKey: ['caixa-extrato'] });
      const qtd = resultados.length;
      toast.success(`${qtd} lançamento(s) processado(s)`);
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
};
